import {
  type CarrierMapping,
  CarrierMappingMatchType,
  type CarrierMappingTrackingCodeFilter,
} from "./carrier-mapping-types";

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const MATCH_TYPE_TO_PATTERN: Record<
  CarrierMappingMatchType,
  (value: string) => RegExp | null
> = {
  [CarrierMappingMatchType.EXACT]: (v) =>
    new RegExp(`^${escapeRegex(v)}$`, "i"),
  [CarrierMappingMatchType.STARTS_WITH]: (v) =>
    new RegExp(`^${escapeRegex(v)}`, "i"),
  [CarrierMappingMatchType.ENDS_WITH]: (v) =>
    new RegExp(`${escapeRegex(v)}$`, "i"),
  [CarrierMappingMatchType.CONTAINS]: (v) => new RegExp(escapeRegex(v), "i"),
  [CarrierMappingMatchType.CATCH_ALL]: () => /.*/i,
  [CarrierMappingMatchType.UNSPECIFIED]: () => /^(other)?$/i,
  [CarrierMappingMatchType.REGEX]: (v) => {
    try {
      return new RegExp(v, "i");
    } catch {
      return null;
    }
  },
};

function matchTypeToRegex(
  value: string,
  matchType: CarrierMappingMatchType,
): RegExp | null {
  return MATCH_TYPE_TO_PATTERN[matchType](value);
}

function trackingCodeMatches(
  trackingCode: string | undefined,
  filter: CarrierMappingTrackingCodeFilter | null | undefined,
): boolean {
  if (!filter) {
    return true;
  }
  const regex = matchTypeToRegex(filter.value, filter.matchType);
  return regex?.test(trackingCode ?? "") ?? false;
}

/**
 * Evaluate an ordered list of carrier mapping rules against a carrier string
 * and optional tracking code. Returns the first matching rule's targetCarrier,
 * or the original carrier if no rule matches.
 */
export function applyCarrierMappingRules({
  carrier,
  trackingCode,
  rules,
}: {
  carrier: string | undefined;
  trackingCode?: string;
  rules: readonly CarrierMapping[];
}): string | undefined {
  const carrierToTest = carrier ?? "";

  for (const rule of rules) {
    const carrierRegex = matchTypeToRegex(rule.value, rule.matchType);
    if (
      carrierRegex?.test(carrierToTest) &&
      trackingCodeMatches(trackingCode, rule.trackingCode)
    ) {
      return rule.targetCarrier;
    }
  }

  return carrier;
}
